import * as React from 'react';
import AllProfiles from '../profiles/AllProfiles';
import ProfileFilters from '../profiles/ProfileFilters';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Header from '../header/Header';
import './Favourite.css';
import useAuth from '../hooks/useAuth';
import { createFilterQuery, getFavourites } from '../../services/profile';
import { FAILED_TO_FETCH_PROFILES_MESSAGE, FAVOURITES_NO_PROFILE_PRIMARY, FAVOURITES_NO_PROFILE_SECONDARY, PAGE_SIZE } from '../constants/Constants';
import { ToastContainer, toast } from "react-toastify";
import { cacheFilterState } from "../common/utilities/FilterTranslator";
import { LOCALSTORAGE_FAVOURITES_PAGENUMBER } from '../../services/Constants';

function Favourite() {
	const getOrDefaultLocalStoragePage = () => {
	  const stored = localStorage.getItem(LOCALSTORAGE_FAVOURITES_PAGENUMBER);
	  return stored ? Number(stored) : 1;
	};
    const { profile, getToken, filterState, setFilterState, areFiltersLoaded } = useAuth();
	const [filteredProfiles, setFilteredProfiles] = React.useState([]);
	const [localFilterState, setLocalFilterState] = React.useState(filterState);
	const [searchText, setSearchText] = React.useState('');
	const searchTextRef = React.useRef(searchText);
	const timerRef = React.useRef(null);
	const [currentPage, setCurrentPage] = React.useState(getOrDefaultLocalStoragePage());
	const [totalPages, setTotalPages] = React.useState(1);
	const [loading, setLoading] = React.useState(false);
	const headerRef = React.useRef(null);
	const [firstRenderCompleted, setFirstRenderCompleted] = React.useState(false);

	// Sync local filter state with global filter state on first load
	React.useEffect(() => {
		if (areFiltersLoaded) {
			fetchFavourites(1, true);
		}
		// eslint-disable-next-line
	}, [areFiltersLoaded]);

	const invokeApplyFilterWithDelay = (page , firstRender, timeoutPeriod = null) => {
		if(timerRef.current) {
			clearTimeout(timerRef.current);
		}
		timerRef.current = setTimeout(() => {
			fetchFavourites(page ?? 1, firstRender);
		}, timeoutPeriod);
	}

	const fetchFavourites = async (page = 1, firstRender = false) => {
		if(!areFiltersLoaded || loading) return;	
		if(!firstRender && !firstRenderCompleted) return;

		!loading && setLoading(true);
		const auth = getToken();
		let filters = localFilterState ?? filterState;
		const searchTextToUse = searchTextRef.current;
		if (auth) {
			try {
				// Build filters for backend
				let filterArr = [];
				let age = filters.ageFilterState, height = filters.heightFilterState;
				Object.keys(filters).forEach((key) => {
					if (key !== 'ageFilterState' && key !== 'heightFilterState') {
						filterArr.push(filters[key]);
					}
				});
				let listOfFilters = createFilterQuery(filterArr, age, profile.gender, height);
				const response = await getFavourites(auth.token, page, PAGE_SIZE, listOfFilters, searchTextToUse);
				if (response.data.errors) {
					toast.error(FAILED_TO_FETCH_PROFILES_MESSAGE, { timeOut: 4000 });
					setFilteredProfiles([]);
					setCurrentPage(1);
					setTotalPages(1);
				} else {
					const data = response.data.data.getFavourites;
					setFilteredProfiles(data.profiles);
					data.current_page !== currentPage && setCurrentPage(data.current_page);
					setTotalPages(data.total_pages);
					// Cache and sync filter state
					cacheFilterState({ ...filters });
					setFilterState({ ...filters });
					if (firstRender) setLocalFilterState({ ...filters });
					toast.success("Favourites loaded successfully!", { timeOut: 2000 });
					setTimeout(() => {
						if (headerRef.current) {
							headerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
						}
					}, 500);
				}
			} catch (error) {
				console.error('error in filtering profiles on favourites page', error);
				setFilteredProfiles([]);
				setCurrentPage(1);
				setTotalPages(1);
				toast.error(FAILED_TO_FETCH_PROFILES_MESSAGE, { timeOut: 4000 });
			} finally {
				!firstRenderCompleted && setFirstRenderCompleted(true);
				setLoading(false);
			}
		}
	};

	const handlePageChange = (event, value) => {
		setCurrentPage(value);
	};	

	const handleApplyFilter = () => {
		fetchFavourites(1, false);
	};

	const resetSearchText = () => {
		setSearchText("");
	};

	React.useEffect(() => {
		searchTextRef.current = searchText;
		invokeApplyFilterWithDelay(1, false, 1000);
	}, [searchText]);

	React.useEffect(() => {
		fetchFavourites(currentPage, false);
		localStorage.setItem(LOCALSTORAGE_FAVOURITES_PAGENUMBER, currentPage);
	}, [currentPage]);

	React.useEffect(() => {
		return () => {
			if(timerRef.current) {
				clearTimeout(timerRef.current);
			}
		}
	}, []);

	return (
		<>
			<ToastContainer />
			<div ref={headerRef}>
				<Header
					heading='Favourites'
					subtext=''
					searchText={searchText}
					setSearchText={setSearchText}
					invokeApplyFilterWithDelay={invokeApplyFilterWithDelay}
				/>
			</div>

			<div className='container-fluid mt-1 mb-1'>
				<div className='row home-row'>
					<div className='p-1 col-xs-12 col-sm-12 col-md-4 col-lg-3 relative'>
						<ProfileFilters
							applyProfileFilter={handleApplyFilter}
							filterState={localFilterState}
							setFilterState={setLocalFilterState}
							appliedFilters={filterState}
							resetSearchText={resetSearchText}
						/>
					</div>
					<div className='col p-1 home-profiles'>
						{loading || !firstRenderCompleted ? (
							<div className="flex justify-center">
								<span className="loading loading-dots loading-lg"></span>
							</div>
						) : (
							<AllProfiles
								profiles={[...filteredProfiles]}
								primaryText={FAVOURITES_NO_PROFILE_PRIMARY}
								secondaryText={FAVOURITES_NO_PROFILE_SECONDARY}
							/>
						)}
					</div>
				</div>
				<div className='row home-row'>
					<div className='col'>
						<div
							style={{
								placeContent: 'center',
								display: 'flex',
							}}
						>
							<Stack spacing={2}>
								<Pagination
									color='primary'
									count={totalPages}
									page={currentPage}
									onChange={handlePageChange}
									showFirstButton
									showLastButton
								/>
							</Stack>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export default Favourite;
